import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, Bot, RotateCcw, Waves, ArrowRight } from "lucide-react";

/**
 * ChatbotSection — Home Exchange Copilot AI
 * Chat minimale: chiede SOLO nome e cognome, poi porta alla pagina /valutazione
 * (raggiungibile anche direttamente via QR code).
 */

const GREETING = "Benvenuto! Sono il Copilota Intelligente di Enea Specialist (Gruppo Vela). La tua casa è pronta per il mercato o rischia di restare invenduta per mesi? Avvio la diagnostica del tuo immobile: mi basta un solo dato per iniziare — come ti chiami? (nome e cognome)";

function nowIso() { return new Date().toISOString(); }

const QUESTION_STARTERS = ["come ", "cosa ", "cos'", "che cos", "perché", "perche ", "quanto ", "quanti ", "quante ", "quale ", "quali ", "posso ", "puoi ", "potete ", "dove ", "quando ", "mi spieghi", "spiegami", "spiegatemi", "non capisco", "non ho capito", "non so ", "cosa significa", "aiuto", "in che senso", "e se ", "esempio"];
function isQuestionText(t) {
  const s = String(t || "").trim().toLowerCase();
  if (!s) return false;
  if (s.includes("?")) return true;
  return QUESTION_STARTERS.some((k) => s.startsWith(k));
}

export default function ChatbotSection({ beta = false }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([{ role: "bot", text: GREETING, ts: nowIso() }]);
  const [inputValue, setInputValue] = useState("");
  const [typing, setTyping] = useState(false);
  const [asking, setAsking] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [qaHistory, setQaHistory] = useState([]);
  const qaSessionRef = useRef(null);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  const valPath = beta ? "/valutazione?beta=1" : "/valutazione";

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight + 400;
    }
  }, [messages, typing]);

  const pushBot = async (text, delay = 650) => {
    setTyping(true);
    await new Promise((r) => setTimeout(r, delay));
    setMessages((m) => [...m, { role: "bot", text, ts: nowIso() }]);
    setTyping(false);
  };
  const pushUser = (text) => setMessages((m) => [...m, { role: "user", text, ts: nowIso() }]);

  const askCopilot = async (q) => {
    if (asking) return;
    pushUser(q);
    setAsking(true);
    setTyping(true);
    try {
      if (!qaSessionRef.current) qaSessionRef.current = Math.random().toString(36).slice(2) + Date.now().toString(36);
      const { data: res } = await api.post("/chatbot-ask", {
        question: q,
        step_id: "owner_name",
        session_id: qaSessionRef.current,
        history: qaHistory.slice(-6),
        context: {},
      }, { timeout: 60000 });
      const answer = res?.answer || "Non ho una risposta precisa su questo punto, ma il nostro perito potrà chiarirlo nel report. Intanto dimmi il tuo nome e cognome per iniziare.";
      setTyping(false);
      setMessages((m) => [...m, { role: "bot", text: answer, ts: nowIso() }]);
      setQaHistory((h) => [...h, { q, a: answer }]);
    } catch (e) {
      setTyping(false);
      setMessages((m) => [...m, { role: "bot", text: "Non riesco a rispondere in questo momento, riprova tra poco. Puoi comunque scrivermi il tuo nome e cognome per iniziare la valutazione.", ts: nowIso() }]);
    } finally {
      setAsking(false);
    }
  };

  const goToValutazione = (name) => {
    if (name) {
      try { sessionStorage.setItem("hec_owner_name", name); } catch (e) { /* noop */ }
    }
    navigate(valPath, { state: name ? { ownerName: name } : undefined });
  };

  const submitInput = async () => {
    if (typing || asking || redirecting) return;
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (isQuestionText(trimmed)) {
      setInputValue("");
      await askCopilot(trimmed);
      return;
    }
    if (trimmed.length < 3) { toast.error("Inserisci nome e cognome."); return; }
    pushUser(trimmed);
    setInputValue("");
    setRedirecting(true);
    const first = trimmed.split(" ")[0];
    await pushBot(`Perfetto ${first}! Ti porto subito alla pagina di valutazione: lì trovi la scheda con tutti i dati dell'immobile — ci vogliono ~2 minuti. 🚀`, 700);
    setTimeout(() => goToValutazione(trimmed), 1300);
  };

  const restart = () => {
    setMessages([{ role: "bot", text: GREETING, ts: nowIso() }]);
    setInputValue("");
    setQaHistory([]);
    setAsking(false);
    setRedirecting(false);
    qaSessionRef.current = null;
  };

  return (
    <section id="audit" className="relative py-24 lg:py-32 bg-white border-t border-slate-100 overflow-hidden" data-testid="chatbot-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-[#A9D6E5]/25 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#14B8A6]/10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-y-10 gap-x-2 md:gap-10 lg:gap-14 items-start">
          {/* Left column — pitch */}
          <div className="col-span-12 lg:col-span-5">
            <div className="enea-eyebrow flex items-center gap-3 mb-5">
              <Waves className="w-4 h-4 text-[#14B8A6]" />
              <span>06 · Copilota Intelligente</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-[44px] font-display font-semibold leading-tight text-[#0b2545]">
              Parla con Home Exchange
              <span className="block mt-2 text-[#14B8A6] font-medium">Copilot AI.</span>
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Dimmi solo come ti chiami: al resto pensa la scheda intelligente della pagina
              di valutazione. Indirizzo verificato sulle mappe, range di valore sui
              min–max OMI ufficiali e dossier trasmesso al team estimativo di Gruppo Vela.
            </p>
            <ul className="mt-8 space-y-4 text-sm">
              {[
                ["🔁", "Sincronizzazione predittiva vendita+acquisto"],
                ["📊", "Valutazione MCM tecnica e difendibile"],
                ["🏛️", "Quotazioni OMI · Agenzia delle Entrate"],
                ["⏱️", "Analisi preliminare entro 24 ore"],
              ].map(([e, t]) => (
                <li key={t} className="flex items-start gap-3 text-slate-700">
                  <span className="w-8 h-8 rounded-full bg-[#A9D6E5]/40 flex items-center justify-center text-base">{e}</span>
                  <span className="pt-1.5">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex items-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#14B8A6] opacity-70" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#14B8A6]" />
                </span>
                <span>Copilota online</span>
              </div>
              <span>·</span>
              <span>Powered by Gruppo Vela</span>
            </div>
          </div>

          {/* Right column — mini chat */}
          <div className="col-span-12 lg:col-span-7">
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-tr from-[#A9D6E5]/40 via-white to-[#14B8A6]/15 rounded-3xl blur-xl" />
              <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl shadow-slate-200/60 overflow-hidden" data-testid="chatbot-window">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-[#0b2545] text-white">
                  <div className="flex items-center gap-3">
                    <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-[#A9D6E5] to-[#14B8A6] flex items-center justify-center">
                      <Bot className="w-5 h-5 text-[#0b2545]" />
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#14B8A6] border-2 border-[#0b2545]" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold tracking-tight">Home Exchange Copilot AI</div>
                      <div className="text-[11px] text-[#A9D6E5]">Assistente virtuale · Gruppo Vela</div>
                    </div>
                  </div>
                  {messages.length > 1 && !redirecting && (
                    <button type="button" onClick={restart}
                      className="text-[11px] uppercase tracking-[0.14em] font-medium text-white/70 hover:text-white flex items-center gap-1.5"
                      data-testid="chatbot-restart">
                      <RotateCcw className="w-3.5 h-3.5" /> Ricomincia
                    </button>
                  )}
                </div>

                {/* Scroller */}
                <div ref={scrollerRef}
                  className="relative h-[380px] overflow-y-auto px-5 py-6 bg-gradient-to-b from-[#F0F9FC] to-white"
                  data-testid="chatbot-scroller">
                  <div className="space-y-4">
                    {messages.map((m, i) => (
                      <ChatBubble key={i} role={m.role} text={m.text} />
                    ))}
                    {typing && <TypingBubble />}
                    {redirecting && !typing && (
                      <div className="flex justify-start" data-testid="chatbot-redirecting">
                        <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#14B8A6]/10 border border-[#14B8A6]/30 text-xs font-medium text-[#0b2545]">
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-[#14B8A6]" /> Apro la pagina di valutazione…
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Input */}
                <div className="border-t border-slate-100 px-4 pt-2 bg-white flex items-center gap-1.5 text-[10px] text-slate-400" data-testid="chatbot-question-hint">
                  <Sparkles className="w-3 h-3 text-[#14B8A6] flex-shrink-0" />
                  <span>Hai un dubbio? Puoi anche fare una domanda al Copilot (es. &ldquo;Come funziona la valutazione?&rdquo;)</span>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); submitInput(); }}
                  className="px-4 py-3 bg-white flex items-end gap-3"
                  data-testid="chatbot-input-form">
                  <input ref={inputRef} type="text" value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Es. Mario Rossi"
                    disabled={redirecting}
                    className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50/70 text-[#0b2545] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/40 focus:border-[#14B8A6] transition text-sm h-11 disabled:opacity-60"
                    data-testid="chatbot-input" />
                  <button type="submit"
                    disabled={typing || asking || redirecting || !inputValue.trim()}
                    className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#0b2545] to-[#14B8A6] text-white flex items-center justify-center flex-shrink-0 disabled:opacity-50 hover:opacity-95 shadow-md"
                    data-testid="chatbot-send">
                    {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </form>

                {/* Direct link to the form */}
                <div className="px-4 pb-3 bg-white">
                  <button type="button" onClick={() => goToValutazione(null)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#14B8A6]/40 bg-[#14B8A6]/[0.06] text-[#0d9488] text-xs font-semibold hover:bg-[#14B8A6]/10 transition"
                    data-testid="chatbot-goto-valutazione">
                    Preferisci saltare la chat? Vai direttamente alla scheda di valutazione
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Footer note */}
                <div className="px-5 py-2.5 bg-slate-50/70 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-[#14B8A6]" /> Powered by AI · Gruppo Vela</span>
                  <span className="hidden sm:inline">Dati protetti · GDPR compliant</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChatBubble({ role, text }) {
  const isBot = role === "bot";
  return (
    <div className={`flex ${isBot ? "justify-start" : "justify-end"}`} data-testid={`chatbot-msg-${role}`}>
      <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm whitespace-pre-line ${
        isBot ? "bg-white border border-slate-200 text-[#0b2545] rounded-tl-sm"
             : "bg-[#0b2545] text-white rounded-tr-sm"
      }`}>{text}</div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start" data-testid="chatbot-typing">
      <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-tl-sm shadow-sm flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-[#14B8A6] animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2 h-2 rounded-full bg-[#14B8A6] animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-2 h-2 rounded-full bg-[#14B8A6] animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}
